import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Instagram, Facebook, Twitter } from 'lucide-react';
import WhatsAppButton from './WhatsAppButton';
import ThreadsIcon from '../icons/ThreadsIcon';

const Footer: React.FC = () => {
  return (
    <footer className="relative bg-charcoal text-white overflow-hidden">
      {/* Decorative gradient orbs */}
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-coral/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-teal/5 rounded-full blur-[100px]" />

      {/* Top gradient line */}
      <div className="h-px bg-gradient-to-r from-transparent via-coral/40 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center space-x-3 mb-5 group">
              <img
                src="/logo.png"
                alt="Suitcase Muse Logo"
                className="w-12 h-12 object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <div>
                <span className="block font-nunito font-bold text-xl">Suitcase Muse</span>
                <p className="text-sm text-gray-400">Styling Experts</p>
              </div>
            </Link>
            <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
              India's premier styling service for travel, personal, and event looks. Powered by expert stylists and cutting-edge AI insights.
            </p>
            <WhatsAppButton service="consultation" className="bg-coral hover:bg-coral/90 rounded-xl">
              Start Your Style Journey
            </WhatsAppButton>

            {/* Social Media */}
            <div className="mt-8">
              <div className="flex space-x-3">
                {[
                  { href: 'https://www.instagram.com/suitcase.muse/', icon: Instagram, label: 'Instagram' },
                  { href: 'https://www.facebook.com/profile.php?id=61578727394782', icon: Facebook, label: 'Facebook' },
                  { href: 'https://twitter.com/suitcasemuse', icon: Twitter, label: 'Twitter' },
                  { href: 'https://www.threads.com/@suitcase.muse', icon: ThreadsIcon, label: 'Threads' },
                ].map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-coral hover:bg-coral/10 hover:border-coral/30 transition-all duration-300"
                    aria-label={`Follow us on ${social.label}`}
                  >
                    <social.icon size={18} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-nunito font-semibold text-lg mb-5">Services</h3>
            <nav className="space-y-3">
              {[
                { to: '/travel-styling', label: 'Travel Styling' },
                { to: '/event-styling', label: 'Event Styling' },
                { to: '/personal-styling', label: 'Personal Styling' },
                { to: '/personal-shopping', label: 'Personal Shopping' },
                { to: '/ai-analysis', label: 'AI Style Analysis' },
                { to: '/services', label: 'Pricing & Plans' },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="block text-gray-400 hover:text-coral transition-colors duration-200">
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-nunito font-semibold text-lg mb-5">Company</h3>
            <nav className="space-y-3">
              {[
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact' },
                { to: '/vendors', label: 'Partner with Us' },
                { to: '/careers', label: 'Careers' },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="block text-gray-400 hover:text-coral transition-colors duration-200">
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-nunito font-semibold text-lg mb-5">Get In Touch</h3>
            <div className="space-y-4">
              <a href="tel:+918826167100" className="flex items-center space-x-3 group">
                <div className="w-9 h-9 rounded-xl bg-coral/10 flex items-center justify-center group-hover:bg-coral/20 transition-colors">
                  <Phone size={16} className="text-coral" />
                </div>
                <span className="text-gray-400 group-hover:text-white transition-colors">+91 8826 167 100</span>
              </a>
              <a href="mailto:suitcasemuse@gmail.com" className="flex items-center space-x-3 group">
                <div className="w-9 h-9 rounded-xl bg-coral/10 flex items-center justify-center group-hover:bg-coral/20 transition-colors">
                  <Mail size={16} className="text-coral" />
                </div>
                <span className="text-gray-400 group-hover:text-white transition-colors">suitcasemuse@gmail.com</span>
              </a>
            </div>

            {/* Payment trust */}
            <div className="mt-8 p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wider">Secured by</div>
              <div className="flex items-center gap-3 text-gray-400 text-sm">
                <span className="font-semibold">Razorpay</span>
                <span className="text-gray-600">|</span>
                <span>SSL Encrypted</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-14 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-gray-500 text-sm">
                &copy; {new Date().getFullYear()} Suitcase Muse. All rights reserved.
              </p>
              <p className="text-gray-500 text-xs mt-1">
                A brand of <span className="font-medium text-gray-400">Muse Globaltech Ventures Private Limited</span>
              </p>
              <p className="text-gray-600 text-xs mt-1">
                Website developed by{' '}
                <a
                  href="https://bindalinfotech.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-coral/70 hover:text-coral transition-colors duration-200"
                >
                  Bindal Infotech
                </a>
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              {[
                { to: '/shipping', label: 'Shipping' },
                { to: '/returns', label: 'Refunds' },
                { to: '/privacy', label: 'Privacy' },
                { to: '/terms', label: 'Terms' },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="text-gray-500 hover:text-coral text-sm transition-colors duration-200">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
